import React, {ReactElement, MouseEvent} from "react";

import translate from "/src/utils/translate";
import {css, generateKeyId, visibleForLocale} from "/src/utils/utils";
import {LOCALE} from "/src/config";

import LinkTag from "../../buttons/link-tag/LinkTag";
import Icon from "../../icon/Icon";
import Image, {ImageObject} from "../../image/Image";

import ChevronRightIcon from "/src/svg/chevron-right.svg";

import * as styles from "./mobile-navigation-item.module.scss";

type NavNodes =
  Queries.ContentfulNavComponentQueryQuery["allContentfulSuperMainNavBars"]["nodes"][0];

type NavTab = NonNullable<NavNodes["navTabs"]>[0];

interface MobileNavigationItemProps {
  menuIndex: number;
  navTab: NavTab;
  disabled: boolean;
  open: boolean;
  onDrawerClose: (idx: number) => void;
  onNavClick: (e: MouseEvent<HTMLButtonElement>) => void;
  onDrawerTransitionEnd: () => void;
}

const MobileNavigationItem = (
  props: MobileNavigationItemProps
): ReactElement | null => {
  const {
    menuIndex,
    navTab,
    disabled,
    open,
    onNavClick,
    onDrawerClose,
    onDrawerTransitionEnd,
  } = props;

  const drawerRef = React.useRef<null | HTMLDivElement>(null);

  // this is to avoid the flashing glitch of drawer on reload
  const setDrawerVisibility = React.useCallback(
    (isEnd: boolean = false) => {
      if (drawerRef.current) {
        drawerRef.current.style.visibility = open ? "visible" : "hidden";

        if (isEnd && onDrawerTransitionEnd) {
          onDrawerTransitionEnd();

          // this is to avoid funky drawer render being out of viewport on safari
          // event loop 101
          window.requestAnimationFrame(() => {
            if (drawerRef.current) {
              // get first anchor tag and focus
              const firstATag = drawerRef.current.querySelector("a");

              if (firstATag) {
                firstATag.focus();
              }
            }
          });
        }
      }
    },
    [onDrawerTransitionEnd, open]
  );

  // watch disabled prop
  // force drawer to hidden and don't wait for animation to end when it's disabled
  React.useEffect(() => {
    if (drawerRef.current) {
      drawerRef.current.style.visibility = "hidden";
    }
  }, [disabled]);

  // watch open status, we only want to set the visibility on open
  // so that the animation will play correctly
  React.useEffect(() => {
    if (open) {
      setDrawerVisibility();
    }
  }, [open, setDrawerVisibility]);

  if (!navTab) {
    return null;
  }

  if (navTab && !!navTab.url === true) {
    return (
      <LinkTag
        tabIndex={-1}
        role="menuitem"
        classes={css(styles.mobileNavigationItem__navItem)}
        href={navTab.url!}
      >
        <span>{navTab.tabTitle}</span>
      </LinkTag>
    );
  }

  // there is only 3 columns, we are just going to hard code this
  const colItems = [
    {
      title: navTab?.column1Title,
      items: navTab?.column1SubItems,
    },
    {
      title: navTab?.column2Title,
      items: navTab?.column2SubItems,
    },
    {
      title: navTab?.column3Title,
      items: navTab?.column3SubItems,
    },
  ];

  return (
    // ref: https://stackoverflow.com/a/59216026
    <>
      <button
        onClick={onNavClick}
        type="button"
        className={css(
          styles.mobileNavigationItem__navItem,
          styles.mobileNavigationItem__navItem__btn
        )}
        aria-controls={`sub-nav-drawer-${menuIndex}`}
        aria-expanded={open}
        aria-label={`${navTab?.tabTitle || ""} ${translate(
          "Navigation drawer"
        )}`}
      >
        <span>{navTab?.tabTitle}</span>
        <Icon
          svg={ChevronRightIcon}
          classes={styles.mobileNavigationItem__navIcon}
        />
      </button>

      <div
        onTransitionEnd={() => setDrawerVisibility(true)}
        ref={drawerRef}
        id={`sub-nav-drawer-${menuIndex}`}
        className={css(
          styles.mobileNavigationItem__drawer,
          open ? styles.mobileNavigationItem__drawerOpen : ""
        )}
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
      >
        <div
          className={css(
            styles.mobileNavigationItem__navItem,
            styles.mobileNavigationItem__navItemDrawer
          )}
        >
          <span>{navTab.tabTitle}</span>
          <button
            aria-label={translate("close drawer menu")}
            onClick={() => onDrawerClose(-1)}
            type="button"
            className={css(styles.mobileNavigationItem__drawer__closeBtn)}
          >
            <Icon
              svg={ChevronRightIcon}
              classes={css(
                styles.mobileNavigationItem__navIcon,
                styles.mobileNavigationItem__navIconDrawer
              )}
            />
          </button>
        </div>
        <div className={styles.mobileNavigationItem__drawer__navWrapper}>
          {colItems.map((item, colIndex) => {
            const colSubItems = (item.items || []).filter(item => {
              const visibleIn: Array<string> =
                item && item.visibleIn ? (item.visibleIn as Array<string>) : [];

              return visibleForLocale(visibleIn, LOCALE);
            });

            const colTitle = item.title;

            if (colSubItems.length === 0) {
              return null;
            }

            return (
              <div
                key={generateKeyId(`drawer-menu-nav-col-items-${colIndex}`)}
                className={css(styles.mobileNavigationItem__drawer__navGroup)}
              >
                {colTitle && (
                  <h2
                    className={css(
                      styles.mobileNavigationItem__drawer__navGroupTitle,
                      "h5"
                    )}
                  >
                    {colTitle}
                  </h2>
                )}
                <ul
                  role="menu"
                  className={styles.mobileNavigationItem__drawer__navList}
                >
                  {colSubItems.map((item: any, i: number) => {
                    return (
                      <li
                        role="none"
                        className={css(
                          styles.mobileNavigationItem__drawer__navItem
                        )}
                        key={generateKeyId(
                          `drawer-nav-subitems-1-${item?.menuItemTitle}-${i}`
                        )}
                      >
                        <LinkTag
                          classes={css(
                            "h5",
                            styles.mobileNavigationItem__drawer__navLink
                          )}
                          tabIndex={-1}
                          role="menuitem"
                          href={item?.menuItemLink!}
                        >
                          <div
                            className={
                              styles.mobileNavigationItem__drawer__navContent
                            }
                          >
                            {item?.menuItemIcon != null && (
                              <Image
                                image={item.menuItemIcon as ImageObject}
                                classes={
                                  styles.mobileNavigationItem__drawer__navIcon
                                }
                              />
                            )}
                            <div
                              className={css(
                                styles.mobileNavigationItem__drawer__navCopy
                              )}
                            >
                              <span
                                className={
                                  styles.mobileNavigationItem__drawer__navText
                                }
                              >
                                {item?.menuItemTitle}
                              </span>
                            </div>
                          </div>
                        </LinkTag>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default MobileNavigationItem;
